import { useFormik } from 'formik';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  Card,
  Container,
} from '@mui/material';
import zxcvbn from 'zxcvbn';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Warning } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import forgotPasswordService from '../api/services/forgotPassword';

const Password = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [strength, setStrength] = useState<ReturnType<typeof zxcvbn> | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    onSubmit: async (values) => {
      if (values.password !== values.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (values.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      try {
        const res = await forgotPasswordService.post({
          token,
          newPassword: values.password,
        });
        if (res.id) {
          navigate('/login?message=Password reset. You may now log in.');
        } else {
          setError('Error resetting password');
        }
      } catch (e: any) {
        setError(
          e?.response?.data?.message ||
            'This reset link is invalid or has expired.'
        );
      }
    },
  });

  useEffect(() => {
    setStrength(formik.values.password ? zxcvbn(formik.values.password) : null);
  }, [formik.values.password]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ bgcolor: '#f6f9fb' }}
    >
      <Container maxWidth="xs">
        <Card sx={{ p: 4 }}>
          <form onSubmit={formik.handleSubmit}>
            <Stack direction="column" spacing={2}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  DealerScout
                </Typography>
              </Link>
              <Typography variant="h4" sx={{ fontWeight: 700 }} fontSize={32}>
                Reset Password
              </Typography>
              {!token && (
                <Alert severity="warning">
                  This link is missing its reset token.
                </Alert>
              )}
              <TextField
                label="New password"
                fullWidth
                required
                type="password"
                {...formik.getFieldProps('password')}
              />
              <TextField
                label="Confirm password"
                fullWidth
                required
                type="password"
                {...formik.getFieldProps('confirmPassword')}
              />
              {error && (
                <Alert icon={<Warning />} color="error">
                  {error}
                </Alert>
              )}
              {strength && strength.feedback.suggestions.length > 0 && (
                <ul style={{ fontSize: 14, margin: 0, paddingLeft: 20 }}>
                  {strength.feedback.suggestions.map((suggestion) => (
                    <li key={suggestion}>{suggestion}</li>
                  ))}
                </ul>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!token}
              >
                Reset Password
              </Button>
            </Stack>
          </form>
        </Card>
      </Container>
    </Box>
  );
};

export default Password;
